'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { LucideIcon } from '@skin-seoul/design-system';
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react';
import {
  getCartAnalyticsContext,
  useCartRewardPresentation,
  useCartService,
  useUser,
  useRegionCurrency,
  type CartDiscoveryPresentation,
  type CartResponseResponse,
  type Currency,
} from '@skin-seoul/service';
import { usePersonalizationTracker } from '@skin-seoul/utils';
import { useCartDrawer } from '../../../context/CartDrawerContext';
import { useCartAnalytics } from './CartAnalytics';

interface DiscoveryNavigation {
  isDetailOpen?: boolean;
  presentation?: CartDiscoveryPresentation;
  isRefreshing?: boolean;
  isInitialLoading?: boolean;
  open: (title: string, content: ReactNode) => void;
  back: (reason?: string) => void;
}
// Cart sections are also rendered independently (tests/embedded cart); discovery is optional there.
const Context = createContext<DiscoveryNavigation>({
  open: () => {
    /* Standalone cart sections have no discovery navigator. */
  },
  back: () => {
    /* Standalone cart sections have no discovery navigator. */
  },
});
export const useCartDiscovery = () => useContext(Context);

const DISCOVERY_TRANSITION_SECONDS = 0.24;
const UNLOCK_REWARD_TITLE = 'Unlock reward';

const getCurrentTier = (cart: CartResponseResponse['cart'] | undefined) =>
  cart ? getCartAnalyticsContext(cart, 'unlock_reward_page').current_tier : 0;

function DiscoveryPanel({ title, children }: { title: string; children: ReactNode }) {
  const isPresent = useIsPresent();
  const reduceMotion = useReducedMotion();
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // Set the DOM property for embedded React DOM consumers too.
    if (panel.current) {
      panel.current.inert = !isPresent;
    }
  }, [isPresent]);
  return (
    <motion.div
      ref={panel}
      initial={{ x: reduceMotion ? 0 : '100%' }}
      animate={{ x: 0 }}
      exit={{ x: reduceMotion ? 0 : '100%' }}
      transition={{ duration: reduceMotion ? 0 : DISCOVERY_TRANSITION_SECONDS, ease: [0.22, 1, 0.36, 1] }}
      className="absolute inset-0 z-sticky flex min-h-0 flex-col bg-generic-0"
      role="region"
      aria-label={title}
      aria-hidden={!isPresent}
      inert={!isPresent}
      style={{ pointerEvents: isPresent ? 'auto' : 'none' }}
    >
      {children}
    </motion.div>
  );
}

export function CartDiscoveryNavigation({ children, currency }: { children: ReactNode; currency: Currency }) {
  const { country } = useRegionCurrency();
  const actorId = usePersonalizationTracker().getActorId() ?? undefined;
  const { useGetCartSuspenseQuery } = useCartService();
  const { data } = useGetCartSuspenseQuery({ currency });
  const { user, isLoading: isUserLoading } = useUser();
  const { identity, presentation, isRefreshing, isInitialLoading } = useCartRewardPresentation({
    cart: data.cart,
    isAuthenticated: data.isAuthenticated,
    currency,
    country,
    actorId,
    customerId: user?.userId ?? null,
    isUserLoading,
  });
  return (
    <NavigationForIdentity
      key={identity}
      presentation={presentation}
      isRefreshing={isRefreshing}
      isInitialLoading={isInitialLoading}
    >
      {children}
    </NavigationForIdentity>
  );
}
function NavigationForIdentity({
  children,
  presentation,
  isRefreshing,
  isInitialLoading,
}: {
  children: ReactNode;
  presentation?: CartDiscoveryPresentation;
  isRefreshing: boolean;
  isInitialLoading: boolean;
}) {
  const [screen, setScreen] = useState<{ title: string; content: ReactNode } | null>(null);
  const analytics = useCartAnalytics();
  const initialTier = useRef(0);
  const trigger = useRef<HTMLElement | null>(null);
  const backButton = useRef<HTMLButtonElement>(null);
  const bagRegion = useRef<HTMLDivElement>(null);
  const { isOpen } = useCartDrawer();
  // The stable open/back handlers read the current screen and cart from here.
  const latest = useRef({ screen, cart: analytics.cart });
  latest.current = { screen, cart: analytics.cart };
  const { track } = analytics;
  useEffect(() => {
    // Closing the drawer returns the next open to the bag without remounting the subtree.
    if (!isOpen) {
      trigger.current = null;
      setScreen(null);
    }
  }, [isOpen]);
  useEffect(() => {
    if (bagRegion.current) {
      bagRegion.current.inert = screen !== null;
    }
    if (screen) {
      backButton.current?.focus();
    } else if (trigger.current?.isConnected) {
      trigger.current.focus();
    } else if (trigger.current) {
      bagRegion.current?.focus();
    }
  }, [screen]);
  useEffect(() => {
    // A cart mutation can remove the focused offer before returning to the bag.
    if (screen && !presentation && document.activeElement === document.body) {
      backButton.current?.focus();
    }
  }, [screen, presentation]);
  const back = useCallback(
    (reason = 'back') => {
      const { screen: current, cart } = latest.current;
      if (current?.title === UNLOCK_REWARD_TITLE) {
        const currentTier = getCurrentTier(cart);
        track(
          'unlock_reward_page_exited',
          { exit_reason: reason, initial_tier: initialTier.current, reward_reached: currentTier > initialTier.current },
          'unlock_reward_page',
        );
      }
      setScreen(null);
    },
    [track],
  );
  const open = useCallback((title: string, content: ReactNode) => {
    initialTier.current = getCurrentTier(latest.current.cart);
    trigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setScreen({ title, content });
  }, []);
  const isDetailOpen = screen !== null;
  const value = useMemo<DiscoveryNavigation>(
    () => ({ isDetailOpen, presentation, isRefreshing, isInitialLoading, open, back }),
    [isDetailOpen, presentation, isRefreshing, isInitialLoading, open, back],
  );
  return (
    <Context.Provider value={value}>
      <div
        ref={bagRegion}
        role="region"
        aria-label="My bag"
        tabIndex={-1}
        className="flex h-full min-h-0 w-full flex-1 flex-col outline-none"
        inert={screen !== null}
        aria-hidden={screen !== null}
      >
        {children}
      </div>
      <AnimatePresence initial={false}>
        {screen && (
          <DiscoveryPanel key="discovery" title={screen.title}>
            <header className="relative flex h-12 shrink-0 items-center justify-center px-12">
              <button
                type="button"
                ref={backButton}
                aria-label="Back to bag"
                onClick={() => back()}
                className="absolute left-3 top-3 size-6"
              >
                <LucideIcon name="ChevronLeft" size={24} />
              </button>
              <h2 className="text-label-large font-medium">{screen.title}</h2>
            </header>
            <div className="min-h-0 flex-1 overflow-y-auto scrollbar-none" data-vaul-no-drag>
              {screen.content}
            </div>
          </DiscoveryPanel>
        )}
      </AnimatePresence>
    </Context.Provider>
  );
}
